// src/admin/pages/CompanyListPage.jsx — /admin/companies (v212): the customer companies, searchable by name or VAT,
// filters «Disattivate» (include_inactive) and «Senza tenant» (without_workspaces, BE ≥ 289980e — an older backend
// answers 422 UNKNOWN_QUERY_PARAM, worded «non disponibile»). A row opens /admin/companies/:id (the name rides in
// the location state for a company whose purge started, which can no longer be read — decision C5).
// ⛔ A sequence ticket guards every read. On the list `plan_id` / seats are null (not loaded): not shown here.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import AdminTable from '../components/AdminTable.jsx';
import AdminPagination from '../components/AdminPagination.jsx';
import FilterChipGroup from '../components/FilterChipGroup.jsx';
import { listCompanies } from '../companiesApi.js';
import { companyPath } from '../companies/companyModel.js';
import { adminErrorText } from '../adminErrorCopy.js';
import { COLORS, FONT_MONO, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';

const LIMIT = 20;

const chip = (palette) => ({
  padding: '2px 8px', borderRadius: RADII.controlInner, fontSize: '11px', fontWeight: 600,
  background: palette.bg, color: palette.fg, whiteSpace: 'nowrap',
});

export function StatusChip({ isActive, t }) {
  if (isActive === true) return <span style={chip(SEMANTIC.verde)}>{t('companies.active')}</span>;
  if (isActive === false) return <span style={chip(SEMANTIC.attenzione)}>{t('companies.inactive')}</span>;
  return null; // unknown: no guessed status
}

export default function CompanyListPage() {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const ticket = useRef(0);

  const load = useCallback(async () => {
    ticket.current += 1;
    const mine = ticket.current;
    setLoading(true);
    setError('');
    try {
      const data = await listCompanies({
        q: query,
        includeInactive: filters.includes('inactive'),
        withoutWorkspaces: filters.includes('orphans'),
        limit: LIMIT,
        offset,
      });
      if (ticket.current !== mine) return;
      setRows(Array.isArray(data?.customers) ? data.customers : []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (err) {
      if (ticket.current !== mine) return;
      setRows([]);
      setTotal(0);
      setError(adminErrorText(t, err));
    } finally {
      if (ticket.current === mine) setLoading(false);
    }
  }, [query, filters, offset, t]);

  // A new search or filter starts from the first page.
  useEffect(() => { setOffset(0); }, [query, filters]);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: 'legal_name', label: t('companies.columns.name') },
    {
      key: 'vat_number', label: t('companies.columns.vat'), width: '160px',
      render: (row) => <span style={{ fontFamily: FONT_MONO, fontSize: '12px' }}>{row.vat_number || '—'}</span>,
    },
    {
      key: 'is_active', label: t('companies.columns.status'), width: '120px',
      render: (row) => <StatusChip isActive={row.is_active} t={t} />,
    },
    {
      key: 'workspace_count', label: t('companies.columns.tenants'), width: '140px',
      render: (row) => {
        if (typeof row.workspace_count !== 'number') return '—';
        if (row.workspace_count === 0) return <span style={chip(SEMANTIC.neutro)}>{t('companies.orphan')}</span>;
        return row.workspace_count;
      },
    },
  ];

  return (
    <div style={{ padding: '32px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '1100px' }}>
      <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0 }}>{t('companies.title')}</h1>
      <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: '4px 0 24px' }}>{t('companies.subtitle')}</p>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
        <form onSubmit={(e) => e.preventDefault()} style={{ position: 'relative', width: '320px', maxWidth: '100%' }}>
          <Search size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: COLORS.textSecondary }} />
          <input
            type="text"
            aria-label={t('companies.searchPlaceholder')}
            placeholder={t('companies.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 36px', borderRadius: RADII.controlInner,
              border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
              fontSize: '13px', boxSizing: 'border-box', outline: 'none', fontFamily: FONT_SANS,
            }}
          />
        </form>
        <FilterChipGroup
          options={[
            { value: 'inactive', label: t('companies.filterInactive') },
            { value: 'orphans', label: t('companies.filterOrphans') },
          ]}
          value={filters}
          onChange={setFilters}
        />
      </div>

      {error && (
        <div role="alert" style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: RADII.controlInner, background: SEMANTIC.errore.bg, color: SEMANTIC.errore.fg, fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ background: COLORS.surface, borderRadius: RADII.control, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        <AdminTable
          columns={columns}
          rows={rows}
          loading={loading}
          emptyMessage={t('companies.empty')}
          getRowKey={(row) => row.id}
          onRowClick={(row) => navigate(companyPath(row.id), { state: { name: row.legal_name } })}
        />
      </div>
      <AdminPagination total={total} limit={LIMIT} offset={offset} onChange={setOffset} itemLabel={t('companies.itemLabel')} />
    </div>
  );
}
